// src/components/AuthModal.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { auth, db, functions } from '../firebase/firebaseConfig';
import {
    GoogleAuthProvider,
    signInWithPopup,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { FaCheckCircle, FaTimes } from 'react-icons/fa';
import useBodyScrollLock from '../utils/useBodyScrollLock';
import { trackEvent } from '../utils/tracking';

export default function AuthModal({
    onClose,
    onVerificationSuccess = () => {},
    forceVerification = false,
    verificationEmail: forcedVerificationEmail = '',
    onVerificationRequired = () => {},
}) {
    const [isSignUp, setIsSignUp] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
    const [closing, setClosing] = useState(false);
    const [shake, setShake] = useState(false);
    const [isResetMode, setIsResetMode] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [isResetProcessing, setIsResetProcessing] = useState(false);
    const [resetStatus, setResetStatus] = useState(null);
    const [authStage, setAuthStage] = useState('form');
    const [verificationEmail, setVerificationEmail] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [verificationStatus, setVerificationStatus] = useState(null);
    const [isSendingVerification, setIsSendingVerification] = useState(false);
    const [isConfirmingVerification, setIsConfirmingVerification] = useState(false);
    const [verificationExpiresAt, setVerificationExpiresAt] = useState(null);
    const [verificationRemaining, setVerificationRemaining] = useState('');
    const verificationContextRef = useRef('sign_in');
    const forcedVerificationStartedRef = useRef(false);
    const startVerificationFlowRef = useRef(() => {});

    useBodyScrollLock(true);

    // Trigger shake animation on error
    useEffect(() => {
        if (error) {
            setShake(true);
            const timer = setTimeout(() => setShake(false), 300);
            return () => clearTimeout(timer);
        }
    }, [error]);

    useEffect(() => {
        if (!verificationExpiresAt) {
            setVerificationRemaining('');
            return undefined;
        }

        const updateRemaining = () => {
            const diff = verificationExpiresAt.getTime() - Date.now();
            if (Number.isNaN(diff) || diff <= 0) {
                setVerificationRemaining('Expired');
                return;
            }
            const minutes = Math.floor(diff / 60000);
            const seconds = Math.floor((diff % 60000) / 1000);
            const paddedSeconds = seconds.toString().padStart(2, '0');
            setVerificationRemaining(`${minutes}:${paddedSeconds}`);
        };

        updateRemaining();
        const intervalId = window.setInterval(updateRemaining, 1000);
        return () => window.clearInterval(intervalId);
    }, [verificationExpiresAt]);

    const passwordRequirements = {
        length: password.length >= 8,
        upper: /[A-Z]/.test(password),
        lower: /[a-z]/.test(password),
        number: /[0-9]/.test(password),
        symbol: /[^A-Za-z0-9]/.test(password),
    };

    const isPasswordValid = Object.values(passwordRequirements).every(Boolean);
    const isEmailValid = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const isFormValid = isEmailValid(email) && password.length >= 8;
    const isEmailVerificationStage = authStage === 'email-verify';
    const isVerificationStage = isEmailVerificationStage;

    const handleUserDoc = async (userCredential) => {
        const user = userCredential.user;
        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);
        const providerIds = Array.isArray(user.providerData)
            ? user.providerData.map((provider) => provider?.providerId).filter(Boolean)
            : [];
        const inferredProvider = providerIds[0] || user.providerId || 'password';
        const resolvedEmail = (user.email || '').trim();
        const resolvedDisplayName =
            user.displayName || (resolvedEmail ? resolvedEmail.split('@')[0] : '');

        if (!userSnap.exists()) {
            await setDoc(userRef, {
                email: resolvedEmail || null,
                displayName: resolvedDisplayName || null,
                role: 'user',
                createdAt: serverTimestamp(),
                lastLogin: serverTimestamp(),
                emailVerified: Boolean(user.emailVerified),
                authProviders: providerIds,
                primaryAuthProvider: inferredProvider,
            });
        } else {
            await updateDoc(userRef, {
                lastLogin: serverTimestamp(),
                email: resolvedEmail || userSnap.data()?.email || null,
                emailVerified: Boolean(user.emailVerified),
                authProviders: providerIds.length ? providerIds : userSnap.data()?.authProviders || [],
                primaryAuthProvider: inferredProvider,
            });
        }
    };

    const startResetFlow = () => {
        setIsResetMode(true);
        setIsSignUp(false);
        setResetEmail(email);
        setResetStatus(null);
        setError('');
        setShowPasswordRequirements(false);
        setAuthStage('form');
        trackEvent('password_reset_start');
    };

    const exitResetFlow = () => {
        setIsResetMode(false);
        setResetStatus(null);
        setIsResetProcessing(false);
        setResetEmail('');
        setAuthStage('form');
    };

    const parseDate = useCallback((value) => {
        if (!value) return null;
        if (value instanceof Date) {
            return Number.isNaN(value.getTime()) ? null : value;
        }
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }, []);

    const refreshVerificationStatus = useCallback(async () => {
        try {
            const callable = httpsCallable(functions, 'getEmailVerificationStatus');
            const result = await callable();
            const data = result?.data || {};
            const expires = parseDate(data.expiresAt);
            setVerificationExpiresAt(expires);
            if (data.status === 'verified') {
                setVerificationStatus({ type: 'success', message: 'Your email is already verified.' });
            }
            return data;
        } catch (statusError) {
            console.error('Failed to load verification status', statusError);
            return null;
        }
    }, [parseDate]);

    const sendEmailVerificationCode = useCallback(async (triggeredBy = 'auto', overrides = {}) => {
        setIsSendingVerification(true);
        try {
            trackEvent('email_verification_code_requested', {
                context: verificationContextRef.current,
                triggeredBy,
            });
            const callable = httpsCallable(functions, 'requestEmailVerificationCode');
            const resolvedEmail =
                overrides.email ||
                verificationEmail ||
                forcedVerificationEmail ||
                auth.currentUser?.email ||
                '';
            const payload = {
                context: verificationContextRef.current,
            };
            if (resolvedEmail) {
                payload.email = resolvedEmail;
            }
            if (overrides.force) {
                payload.force = true;
            }
            const result = await callable(payload);
            const data = result?.data || {};
            if (data.status === 'already-verified') {
                setVerificationStatus({ type: 'success', message: 'Your email is already verified.' });
                await refreshVerificationStatus();
                trackEvent('email_verification_code_skipped', {
                    context: verificationContextRef.current,
                    reason: 'already-verified',
                });
                return data;
            }
            if (data.email) {
                setVerificationEmail(data.email);
            }
            const expires = parseDate(data.expiresAt);
            setVerificationExpiresAt(expires);
            setVerificationStatus({
                type: 'success',
                message: data.email
                    ? `We sent a six-digit code to ${data.email}.`
                    : 'We sent you a new six-digit code.',
            });
            trackEvent('email_verification_code_sent', {
                context: verificationContextRef.current,
                triggeredBy,
            });
            return data;
        } catch (codeError) {
            console.error('Failed to send verification code', codeError);
            let message = 'We couldn’t send that code. Please try again.';
            if (codeError?.message) {
                message = codeError.message;
            }
            setVerificationStatus({ type: 'error', message });
            trackEvent('email_verification_code_error', {
                context: verificationContextRef.current,
                triggeredBy,
                code: codeError?.code || 'unknown',
            });
            throw codeError;
        } finally {
            setIsSendingVerification(false);
        }
    }, [forcedVerificationEmail, parseDate, refreshVerificationStatus, verificationEmail]);

    const startEmailVerificationFlow = useCallback(
        async ({ email: initialEmail, autoSend = true, context = 'sign_in' } = {}) => {
            forcedVerificationStartedRef.current = true;
            verificationContextRef.current = context;
            setAuthStage('email-verify');
            setVerificationCode('');
            setVerificationStatus({
                type: 'info',
                message: 'Enter the six-digit security code we emailed to verify your access.',
            });
            if (initialEmail) {
                setVerificationEmail(initialEmail);
            }
            const resolvedEmail =
                initialEmail ||
                verificationEmail ||
                forcedVerificationEmail ||
                auth.currentUser?.email ||
                '';
            if (!initialEmail && resolvedEmail) {
                setVerificationEmail(resolvedEmail);
            }
            setVerificationExpiresAt(null);
            setVerificationRemaining('');
            trackEvent('email_verification_started', { context });
            onVerificationRequired(resolvedEmail);
            if (autoSend) {
                try {
                    await sendEmailVerificationCode('auto', { email: resolvedEmail });
                } catch (err) {
                    console.warn('Initial verification send failed', err);
                }
            } else {
                await refreshVerificationStatus();
            }
        },
        [
            forcedVerificationEmail,
            onVerificationRequired,
            refreshVerificationStatus,
            sendEmailVerificationCode,
            verificationEmail,
        ]
    );

    useEffect(() => {
        if (!forceVerification || closing) {
            forcedVerificationStartedRef.current = false;
            return;
        }

        if (forcedVerificationStartedRef.current || isEmailVerificationStage) {
            return;
        }

        let cancelled = false;

        const ensureVerificationNeeded = async () => {
            const currentUser = auth.currentUser;
            if (currentUser?.emailVerified) {
                try {
                    await currentUser.reload();
                } catch (reloadError) {
                    console.warn('Failed to reload already-verified user before resolving verification', reloadError);
                }
                onVerificationSuccess(auth.currentUser || currentUser, {
                    type: 'email',
                    reason: 'already-verified',
                });
                return;
            }

            try {
                const status = await refreshVerificationStatus();
                if (cancelled) {
                    return;
                }

                const alreadyVerified =
                    status?.status === 'verified' ||
                    status?.status === 'already-verified' ||
                    status?.emailVerified === true;

                if (alreadyVerified) {
                    const refreshedUser = auth.currentUser;
                    if (refreshedUser) {
                        try {
                            await refreshedUser.reload();
                        } catch (reloadError) {
                            console.warn('Failed to reload user after verifying via status check', reloadError);
                        }
                        onVerificationSuccess(auth.currentUser || refreshedUser, {
                            type: 'email',
                            reason: 'status-already-verified',
                        });
                    }
                    return;
                }
            } catch (err) {
                console.warn('Failed to confirm verification status before forcing flow', err);
                if (cancelled) {
                    return;
                }
            }

            if (cancelled) {
                return;
            }

            forcedVerificationStartedRef.current = true;

            try {
                await startEmailVerificationFlow({
                    email: forcedVerificationEmail || auth.currentUser?.email || '',
                    context: 'resume_session',
                });
            } catch (err) {
                console.warn('Failed to start forced verification flow', err);
            }
        };

        ensureVerificationNeeded();

        return () => {
            cancelled = true;
        };
    }, [
        closing,
        forceVerification,
        forcedVerificationEmail,
        isEmailVerificationStage,
        onVerificationSuccess,
        refreshVerificationStatus,
        startEmailVerificationFlow,
    ]);

    useEffect(() => {
        startVerificationFlowRef.current = startEmailVerificationFlow;
    }, [startEmailVerificationFlow]);

    const stopVerificationFlow = ({ signOutUser = false } = {}) => {
        forcedVerificationStartedRef.current = false;
        setAuthStage('form');
        setVerificationCode('');
        setVerificationStatus(null);
        setVerificationExpiresAt(null);
        setVerificationRemaining('');
        setVerificationEmail('');
        if (signOutUser) {
            signOut(auth).catch((err) => {
                console.error('Failed to sign out after cancelling verification', err);
            });
        }
    };

    const handleVerificationSubmit = async (event) => {
        event.preventDefault();
        const trimmed = verificationCode.trim();
        if (!/^\d{6}$/.test(trimmed)) {
            setVerificationStatus({ type: 'error', message: 'Enter the six-digit code from your inbox.' });
            return;
        }

        setIsConfirmingVerification(true);
        try {
            trackEvent('email_verification_confirm_attempt', {
                context: verificationContextRef.current,
            });
            const callable = httpsCallable(functions, 'confirmEmailVerificationCode');
            const result = await callable({ code: trimmed });
            const data = result?.data || {};
            if (data.status === 'verified' || data.status === 'already-verified') {
                setVerificationStatus({ type: 'success', message: 'Email verified! Redirecting…' });
                await refreshVerificationStatus();
                if (auth.currentUser) {
                    try {
                        await auth.currentUser.reload();
                        onVerificationSuccess(auth.currentUser, { type: 'email' });
                    } catch (reloadError) {
                        console.error('Failed to reload user after verification', reloadError);
                    }
                }
                trackEvent('email_verification_confirmed', {
                    context: verificationContextRef.current,
                    status: data.status,
                });
                setTimeout(() => {
                    stopVerificationFlow();
                    closeModal(true);
                }, 600);
            } else {
                setVerificationStatus({ type: 'success', message: 'Email verified!' });
                trackEvent('email_verification_confirmed', {
                    context: verificationContextRef.current,
                    status: data.status || 'unknown',
                });
                if (auth.currentUser) {
                    try {
                        await auth.currentUser.reload();
                        onVerificationSuccess(auth.currentUser, { type: 'email' });
                    } catch (reloadError) {
                        console.error('Failed to reload user after verification', reloadError);
                    }
                }
                setTimeout(() => {
                    stopVerificationFlow();
                    closeModal(true);
                }, 600);
            }
        } catch (verifyError) {
            console.error('Verification confirmation failed', verifyError);
            let message = 'That code did not work. Please try again.';
            if (verifyError?.code === 'functions/deadline-exceeded') {
                message = 'That code expired. Request a new one and try again.';
            } else if (verifyError?.code === 'functions/resource-exhausted') {
                message = 'Too many attempts. Request a new code to keep going.';
            } else if (verifyError?.message) {
                message = verifyError.message;
            }
            setVerificationStatus({ type: 'error', message });
            trackEvent('email_verification_confirm_error', {
                context: verificationContextRef.current,
                code: verifyError?.code || 'unknown',
            });
        } finally {
            setIsConfirmingVerification(false);
        }
    };

    const closeModal = (force = false) => {
        if (!force && isEmailVerificationStage) {
            setVerificationStatus((current) =>
                current?.type === 'error'
                    ? current
                    : {
                          type: 'info',
                          message: 'Please verify your email to continue.',
                      }
            );
            setShake(true);
            setTimeout(() => setShake(false), 300);
            return;
        }
        setClosing(true);
        setIsResetMode(false);
        setResetEmail('');
        setResetStatus(null);
        setIsResetProcessing(false);
        setIsSignUp(true);
        setEmail('');
        setPassword('');
        setShowPasswordRequirements(false);
        setError('');
        setAuthStage('form');
        setVerificationEmail('');
        setVerificationCode('');
        setVerificationStatus(null);
        setIsSendingVerification(false);
        setIsConfirmingVerification(false);
        setVerificationExpiresAt(null);
        setVerificationRemaining('');
        setTimeout(onClose, 250);
    };

    const handleGoogleSignIn = async () => {
        const provider = new GoogleAuthProvider();
        setIsProcessing(true);
        setError('');
        try {
            trackEvent('auth_google_start');
            const result = await signInWithPopup(auth, provider);
            await handleUserDoc(result);
            const status = await refreshVerificationStatus();
            const emailVerified = status?.emailVerified;
            trackEvent('auth_google_success', {
                verified: emailVerified ? 'already' : 'skipped',
            });
            if (auth.currentUser) {
                try {
                    await auth.currentUser.reload();
                    onVerificationSuccess(auth.currentUser, { type: 'federated' });
                } catch (reloadError) {
                    console.error('Failed to reload user after Google sign-in', reloadError);
                    onVerificationSuccess(result.user, { type: 'federated' });
                }
            } else {
                onVerificationSuccess(result.user, { type: 'federated' });
            }
            closeModal(true);
            return;
        } catch (err) {
            console.error(err);
            setError('Google sign-in failed. Please try again.');
            trackEvent('auth_google_error', { code: err?.code || 'unknown' });
        } finally {
            setIsProcessing(false);
        }
    };

    const handleEmailPassword = async (e) => {
        e.preventDefault();
        setIsProcessing(true);
        setError('');

        try {
            trackEvent('auth_password_submit', { mode: isSignUp ? 'sign_up' : 'sign_in' });
            if (isSignUp && !isPasswordValid) {
                setError('Password does not meet all requirements.');
                setIsProcessing(false);
                return;
            }

            let userCredential;
            if (isSignUp) {
                userCredential = await createUserWithEmailAndPassword(auth, email, password);
            } else {
                userCredential = await signInWithEmailAndPassword(auth, email, password);
            }
            await handleUserDoc(userCredential);
            const status = await refreshVerificationStatus();
            const emailVerified = status?.emailVerified;
            trackEvent('auth_password_success', {
                mode: isSignUp ? 'sign_up' : 'sign_in',
                emailVerified: emailVerified ? 'yes' : 'no',
            });

            if (!emailVerified) {
                await startEmailVerificationFlow({
                    email: userCredential.user?.email || email,
                    context: isSignUp ? 'sign_up' : 'sign_in',
                });
                return;
            }

            const currentUser = auth.currentUser;
            if (currentUser) {
                try {
                    await currentUser.reload();
                } catch (reloadError) {
                    console.error('Failed to reload user after sign-in', reloadError);
                }
                onVerificationSuccess(currentUser);
            } else {
                onVerificationSuccess(userCredential.user);
            }
            closeModal(true);
            return;
        } catch (err) {
            console.error(err);
            trackEvent('auth_password_error', {
                mode: isSignUp ? 'sign_up' : 'sign_in',
                code: err?.code || 'unknown',
            });
            if (err.code === 'auth/email-already-in-use') {
                setError('Email already exists. Please sign in instead.');
            } else if (err.code === 'auth/wrong-password' || err.code === 'auth/user-not-found') {
                setError('Invalid email or password. Please try again.');
            } else {
                setError('An unexpected error occurred. Please try again.');
            }
        } finally {
            setIsProcessing(false);
        }
    };

    const handlePasswordReset = async (e) => {
        e.preventDefault();
        if (!isEmailValid(resetEmail)) {
            setResetStatus({ type: 'error', message: 'Please enter a valid email address.' });
            return;
        }

        setIsResetProcessing(true);
        setResetStatus(null);
        trackEvent('password_reset_submit');

        try {
            const requestReset = httpsCallable(functions, 'requestPasswordResetEmail');
            await requestReset({ email: resetEmail });
            setResetStatus({
                type: 'success',
                message: 'Check your inbox for a password reset link. It expires in one hour.',
            });
            trackEvent('password_reset_success');
        } catch (err) {
            console.error('Password reset request failed', err);
            setResetStatus({
                type: 'error',
                message: 'We couldn’t send that email right now. Please try again shortly.',
            });
            trackEvent('password_reset_error', { code: err?.code || 'unknown' });
        } finally {
            setIsResetProcessing(false);
        }
    };

    // Shake + fade/scale variants
    const modalVariants = {
        initial: { scale: 0.9, opacity: 0, x: 0 },
        animate: { scale: 1, opacity: 1, x: 0 },
        shake: { scale: 1, opacity: 1, x: [0, -10, 10, -10, 10, 0] },
        exit: { scale: 0.9, opacity: 0, x: 0 },
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <AnimatePresence>
                {!closing && (
                    <motion.div
                        key="auth-modal"
                        variants={modalVariants}
                        initial="initial"
                        animate={shake ? 'shake' : 'animate'}
                        exit="exit"
                        transition={{ duration: 0.25 }}
                        className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md m-4 relative"
                    >
                        {!isVerificationStage && (
                            <button
                                onClick={() => closeModal()}
                                className="absolute top-4 right-4 text-gray-700 hover:text-[var(--gold)] text-2xl z-20"
                            >
                                <FaTimes />
                            </button>
                        )}

                        <h2 className="text-3xl font-bold text-center mb-4 font-serif">
                            {isEmailVerificationStage
                                ? 'Verify Your Email'
                                : isResetMode
                                    ? 'Reset Your Password'
                                    : isSignUp
                                        ? 'Create Your Account'
                                        : 'Welcome Back'}
                        </h2>
                        <p className="text-center text-gray-600 mb-6">
                            {isEmailVerificationStage
                                ? `Enter the six-digit code we sent to ${verificationEmail || 'your inbox'} to keep your account secure.`
                                : isResetMode
                                    ? 'Enter your GatherLens email and we’ll send a secure reset link.'
                                    : isSignUp
                                        ? 'Sign up to create your first event guestbook.'
                                        : 'Sign in to access your dashboard.'}
                        </p>

                        {!isVerificationStage && !isResetMode && (
                            <AnimatePresence>
                                {error && (
                                    <motion.p
                                        initial={{ opacity: 0, y: -5 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -5 }}
                                        className="bg-red-100 text-red-700 p-3 rounded-md mb-4 text-sm text-center"
                                    >
                                        {error}
                                    </motion.p>
                                )}
                            </AnimatePresence>
                        )}

                        {isEmailVerificationStage && verificationStatus && (
                            <p
                                className={`text-sm mb-4 text-center ${
                                    verificationStatus.type === 'success'
                                        ? 'text-green-600'
                                        : verificationStatus.type === 'error'
                                            ? 'text-red-600'
                                            : 'text-gray-600'
                                }`}
                            >
                                {verificationStatus.message}
                            </p>
                        )}

                        {isEmailVerificationStage ? (
                            <form onSubmit={handleVerificationSubmit}>
                                <div className="mb-4">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2" htmlFor="verification-code">
                                        Six-digit code
                                    </label>
                                    <input
                                        id="verification-code"
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        maxLength={6}
                                        value={verificationCode}
                                        onChange={(e) => {
                                            const value = e.target.value.replace(/[^0-9]/g, '');
                                            setVerificationCode(value);
                                        }}
                                        className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--gold)] tracking-[0.4em] text-center text-lg"
                                        placeholder="000000"
                                        required
                                    />
                                    {verificationRemaining && (
                                        <p className="mt-2 text-xs text-center text-gray-500">
                                            {verificationRemaining === 'Expired'
                                                ? 'This code has expired. Request a new one below.'
                                                : `Code expires in ${verificationRemaining}.`}
                                        </p>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    disabled={isConfirmingVerification || verificationCode.length !== 6}
                                    className={`w-full btn-gold py-3 px-6 text-lg disabled:opacity-50 ${
                                        verificationCode.length !== 6 ? 'cursor-not-allowed' : ''
                                    }`}
                                >
                                    {isConfirmingVerification ? 'Verifying…' : 'Confirm & Continue'}
                                </button>

                                <button
                                    type="button"
                                    onClick={() => sendEmailVerificationCode('manual')}
                                    disabled={isSendingVerification}
                                    className="mt-4 w-full border border-[var(--gold)] text-[var(--gold)] py-2.5 px-6 rounded-lg font-semibold hover:bg-[rgba(201,169,119,0.1)] transition disabled:opacity-50"
                                >
                                    {isSendingVerification ? 'Sending code…' : 'Resend code'}
                                </button>

                                <button
                                    type="button"
                                    onClick={() => {
                                        trackEvent('email_verification_cancelled', {
                                            context: verificationContextRef.current,
                                        });
                                        stopVerificationFlow({ signOutUser: true });
                                        setIsSignUp(false);
                                        setEmail('');
                                        setPassword('');
                                        setError('');
                                    }}
                                    className="mt-4 w-full text-sm font-semibold text-gray-600 hover:text-[var(--gold)]"
                                >
                                    Use a different email
                                </button>
                            </form>
                        ) : isResetMode ? (
                            <form onSubmit={handlePasswordReset}>
                                <input
                                    type="email"
                                    value={resetEmail}
                                    onChange={(e) => setResetEmail(e.target.value)}
                                    placeholder="Email Address"
                                    className="w-full px-4 py-3 border rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                                    required
                                />

                                {resetStatus && (
                                    <p
                                        className={`text-sm mb-4 text-center ${
                                            resetStatus.type === 'success'
                                                ? 'text-green-600'
                                                : resetStatus.type === 'error'
                                                    ? 'text-red-600'
                                                    : 'text-gray-600'
                                        }`}
                                    >
                                        {resetStatus.message}
                                    </p>
                                )}

                                <button
                                    type="submit"
                                    disabled={isResetProcessing || !isEmailValid(resetEmail)}
                                    className={`w-full btn-gold py-3 px-6 text-lg disabled:opacity-50 ${
                                        !isEmailValid(resetEmail) ? 'cursor-not-allowed' : ''
                                    }`}
                                >
                                    {isResetProcessing ? 'Sending link...' : 'Send reset link'}
                                </button>
                            </form>
                        ) : (
                            <>
                                <form onSubmit={handleEmailPassword}>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="Email Address"
                                        className="w-full px-4 py-3 border rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                                        required
                                    />
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={(e) => {
                                            setPassword(e.target.value);
                                            setShowPasswordRequirements(e.target.value.length > 0);
                                        }}
                                        placeholder="Password"
                                        className="w-full px-4 py-3 border rounded-lg mb-2 focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                                        required
                                    />

                                    {!isSignUp && (
                                        <div className="text-right mb-4">
                                            <button
                                                type="button"
                                                onClick={startResetFlow}
                                                className="text-sm text-[var(--gold)] hover:underline"
                                            >
                                                Forgot password?
                                            </button>
                                        </div>
                                    )}

                                    {isSignUp && showPasswordRequirements && (
                                        <ul className="text-sm mb-4 ml-2 text-gray-600">
                                            {Object.entries(passwordRequirements).map(([key, valid]) => {
                                                const requirementText = {
                                                    length: 'At least 8 characters',
                                                    upper: 'At least one uppercase letter',
                                                    lower: 'At least one lowercase letter',
                                                    number: 'At least one number',
                                                    symbol: 'At least one symbol',
                                                }[key];
                                                return (
                                                    <li
                                                        key={key}
                                                        className={`flex items-center gap-1 ${
                                                            valid ? 'text-green-600' : ''
                                                        }`}
                                                    >
                                                        {valid && <FaCheckCircle />}
                                                        {requirementText}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={isProcessing || !isFormValid}
                                        className={`w-full btn-gold py-3 px-6 text-lg disabled:opacity-50 ${
                                            !isFormValid ? 'cursor-not-allowed' : ''
                                        }`}
                                    >
                                        {isProcessing ? 'Processing...' : isSignUp ? 'Sign Up' : 'Sign In'}
                                    </button>
                                </form>

                                <div className="flex items-center my-6">
                                    <div className="flex-grow border-t border-gray-300"></div>
                                    <span className="flex-shrink mx-4 text-gray-400">OR</span>
                                    <div className="flex-grow border-t border-gray-300"></div>
                                </div>

                                <button
                                    onClick={handleGoogleSignIn}
                                    disabled={isProcessing}
                                    className="w-full bg-white border border-gray-300 text-gray-700 font-bold py-3 px-6 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center disabled:opacity-50"
                                >
                                    <img
                                        src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                                        alt="Google icon"
                                        className="w-5 h-5"
                                    />
                                    <span className="ml-2">Continue with Google</span>
                                </button>
                            </>
                        )}

                        {isResetMode ? (
                            <p className="text-center mt-6">
                                Ready to sign in?
                                <button
                                    onClick={() => {
                                        exitResetFlow();
                                        setIsSignUp(false);
                                    }}
                                    className="ml-2 font-semibold text-[var(--gold)] hover:underline"
                                >
                                    Return to Sign In
                                </button>
                            </p>
                        ) : isEmailVerificationStage ? (
                            <p className="text-center mt-6 text-sm text-gray-600">
                                Haven’t seen the email yet? Check your spam folder or resend the code above. We only use verification to keep event galleries secure.
                            </p>
                        ) : (
                            <p className="text-center mt-6">
                                {isSignUp ? 'Already have an account? ' : "Don't have an account? "}
                                <button
                                    onClick={() => {
                                        setIsSignUp(!isSignUp);
                                        setError('');
                                        setShowPasswordRequirements(false);
                                    }}
                                    className="font-semibold text-[var(--gold)] hover:underline"
                                >
                                    {isSignUp ? 'Sign In' : 'Sign Up'}
                                </button>
                            </p>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
